function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import gettext from '../gettext';
import lazygettext from '../lazy-gettext';
import { joinValidators, makeValidator } from './validation';
import { isProvided, makeHasMaxLength, makeMatchesRegex } from './general';
import { isRegionRequired, isPostalRequired } from '../intl'; // Some validation specific onfiguration variables

var POSTAL_PATTERNS = {
  US: /^\d{5}(-\d{4})?/,
  DEFAULT: /^[0-9a-zA-Z\- ]*$/
};
var POSTAL_MAXLENGTHS = {
  DEFAULT: 10
};
var POSTAL_ERRORS = {
  required: {
    US: lazygettext('ZIP code is required'),
    DEFAULT: lazygettext('Postal code is required')
  },
  maxLength: {
    DEFAULT: lazygettext('Postal code must be less than 10 characters long')
  },
  invalid: {
    US: lazygettext('Invalid ZIP code'),
    DEFAULT: lazygettext('Invalid Postal code')
  }
};
var REGION_ERRORS = {
  required: {
    US: lazygettext('Please enter a valid State'),
    CA: lazygettext('Please enter a valid Province'),
    IE: lazygettext('Please enter a valid County'),
    AU: lazygettext('Please enter a valid State/Territory'),
    DEFAULT: lazygettext('Please enter a valid State/Province')
  },
  maxLength: {
    US: lazygettext('State must be 30 characters or less'),
    CA: lazygettext('Province must be 30 characters or less'),
    IE: lazygettext('County must be 30 characters or less'),
    AU: lazygettext('State/Territory must be 30 characters or less'),
    DEFAULT: lazygettext('State/Province must be 30 characters or less')
  }
}; // Internal utility functions

var _getInWithDefault = function _getInWithDefault(obj, key) {
  return obj.hasOwnProperty(key) ? obj[key] : obj.DEFAULT;
};

var _applyPrefixToConfig = function _applyPrefixToConfig(prefix, config) {
  return Object.keys(config).reduce(function (prefixedConfig, key) {
    return _objectSpread({}, prefixedConfig, _defineProperty({}, prefix + key, config[key]));
  }, {});
};

var _makePrefixedValidator = function _makePrefixedValidator(prefix, config) {
  return makeValidator(prefix ? _applyPrefixToConfig(prefix, config) : config);
}; // Validation methods for requiring fields


export var addressFieldRequiredValidators = function addressFieldRequiredValidators() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      prefix = _ref.prefix;

  return _makePrefixedValidator(prefix, {
    country: {
      validator: isProvided,
      errorMessage: gettext('Country is required')
    },
    address1: {
      validator: isProvided,
      errorMessage: gettext('Address is required')
    },
    city: {
      validator: isProvided,
      errorMessage: gettext('City is required')
    }
  });
}; // Region and postal field error messages depend on the current value of the country

export var regionRequiredValidator = function regionRequiredValidator() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      prefix = _ref2.prefix,
      country = _ref2.country;

  return _makePrefixedValidator(prefix, {
    region: {
      validator: isProvided,
      errorMessage: _getInWithDefault(REGION_ERRORS.required, country)
    }
  });
};
export var postalRequiredValidator = function postalRequiredValidator() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      prefix = _ref3.prefix,
      country = _ref3.country;

  return _makePrefixedValidator(prefix, {
    postal: {
      validator: isProvided,
      errorMessage: _getInWithDefault(POSTAL_ERRORS.required, country)
    }
  });
};
/**
 * Field validators that do not change depending on the value of the country/region.
 *
 * @param prefix
 */

export var staticAddressFieldValidators = function staticAddressFieldValidators() {
  var _ref4 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      prefix = _ref4.prefix;

  return _makePrefixedValidator(prefix, {
    address1: {
      validator: makeHasMaxLength(100),
      errorMessage: gettext('Address must be less than 100 characters long')
    },
    city: {
      validator: makeHasMaxLength(50),
      errorMessage: gettext('City must be less than 50 characters long')
    }
  });
};
/**
 * Field validators that depend on the current value of the country to do their
 * job.
 *
 * @param prefix
 * @param country
 */

export var dynamicAddressFieldValidators = function dynamicAddressFieldValidators() {
  var _ref5 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      prefix = _ref5.prefix,
      country = _ref5.country;

  return _makePrefixedValidator(prefix, {
    region: {
      validator: makeHasMaxLength(30),
      errorMessage: _getInWithDefault(REGION_ERRORS.maxLength, country)
    },
    postal: [{
      validator: makeHasMaxLength(_getInWithDefault(POSTAL_MAXLENGTHS, country)),
      errorMessage: _getInWithDefault(POSTAL_ERRORS.maxLength, country)
    }, {
      validator: makeMatchesRegex(_getInWithDefault(POSTAL_PATTERNS, country)),
      errorMessage: _getInWithDefault(POSTAL_ERRORS.invalid, country)
    }]
  });
};
/**
 * Address validator for a form. Everything you need to validate an address
 * component in one convenient function. Dynamically validates based on selected country.
 *
 * Important: Must be called with prefix to return the validation function.
 *
 * @param prefix
 */

export var nonRequiredAddressValidator = function nonRequiredAddressValidator() {
  var _ref6 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      prefix = _ref6.prefix;

  return function (values) {
    var validators = [staticAddressFieldValidators({
      prefix: prefix
    }), dynamicAddressFieldValidators({
      prefix: prefix,
      country: values.country
    })];
    return joinValidators.apply(void 0, validators)(values);
  };
};
/**
 * Required address validator for a form. Does everything the nonRequiredAddressValidator
 * does, but adds the logic for required fields. This validator expects the form
 * to receive a prop `postalRequired`, which can be achieved by using the provided
 * `mapAddressStateToFormProps` function below if using the ConnectedAddress
 * component and/or associated actions + reducers.
 *
 * @param prefix
 */

export var requiredAddressValidator = function requiredAddressValidator() {
  var _ref7 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      prefix = _ref7.prefix;

  return function (values) {
    var _ref8 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      countries: []
    },
        countries = _ref8.countries;

    var validators = [nonRequiredAddressValidator({
      prefix: prefix
    }), addressFieldRequiredValidators({
      prefix: prefix
    })];

    if (isRegionRequired(values.country)) {
      validators.push(regionRequiredValidator({
        prefix: prefix,
        country: values.country
      }));
    }

    if (isPostalRequired(countries, values.country)) {
      validators.push(postalRequiredValidator({
        prefix: prefix,
        country: values.country
      }));
    }

    return joinValidators.apply(void 0, validators)(values);
  };
};